import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Armchair, Workflow, Map, Car, Bot } from "lucide-react";
import { EASE, Eyebrow, Reveal } from "./Reveal";

const FEATURES = [
    {
        n: "01", icon: Armchair, badge: "LIVE MAPS", title: "Desk Booking & Allocation",
        body: "Browse live floor plans for Floors 2, 3, 4, and 6. Click a desk, raise an allocation request, and track it through approval and IT setup. Every desk — occupied, available, or reserved — shown in real time.",
    },
    {
        n: "02", icon: Workflow, badge: "PIPELINE", title: "Three-Stage Approval Workflow",
        body: "Every desk request flows through a three-step pipeline: Manager raises it → Admin approves → IT sets up the workstation. Email notifications go out automatically at each stage. Nothing falls through the cracks.",
    },
    {
        n: "03", icon: Map, badge: "REAL TIME", title: "Interactive Floor Plans", id: "floor-plans",
        body: "Live maps of all four office floors. Cabins, meeting rooms, desk clusters, and open seats — all visible and editable by the Admin team in real time. Not a static diagram; an actual working tool.",
    },
    {
        n: "04", icon: Car, badge: "DISPATCH", title: "Car & Transport Booking",
        body: "Request airport pickups, outstation trips, or office transport. Add traveller details, flight time, and drop point — the vendor receives a formatted dispatch email automatically.",
    },
    {
        n: "05", icon: Bot, badge: "AI", title: "Finance & Admin AI Assistant",
        body: "Ask anything: payroll structure, expense submission, leave encashment, corporate insurance, income tax, or office policy. The chatbot reads IDeaS India's own documents — not the internet. No more hunting through email threads.",
    },
];

const SHAPES = {
    desk: (c) => {
        c.strokeRect(20, 26, 56, 22);
        c.beginPath();
        c.moveTo(28, 48); c.lineTo(28, 70);
        c.moveTo(68, 48); c.lineTo(68, 70);
        c.moveTo(20, 37); c.lineTo(76, 37);
        c.stroke();
    },
    flow: (c) => {
        c.beginPath(); c.arc(24, 48, 9, 0, 7); c.stroke();
        c.beginPath(); c.arc(48, 26, 9, 0, 7); c.stroke();
        c.beginPath(); c.arc(72, 48, 9, 0, 7); c.stroke();
        c.beginPath();
        c.moveTo(33, 44); c.lineTo(40, 33);
        c.moveTo(56, 33); c.lineTo(63, 44);
        c.stroke();
    },
    bot: (c) => {
        c.strokeRect(26, 34, 44, 32);
        c.beginPath(); c.moveTo(48, 34); c.lineTo(48, 24); c.stroke();
        c.beginPath(); c.arc(48, 20, 3.5, 0, 7); c.fill();
        c.beginPath(); c.arc(39, 48, 3.5, 0, 7); c.fill();
        c.beginPath(); c.arc(57, 48, 3.5, 0, 7); c.fill();
        c.beginPath(); c.moveTo(38, 58); c.lineTo(58, 58); c.stroke();
    },
};

const GLYPHS = { "01": SHAPES.desk, "02": SHAPES.flow, "05": SHAPES.bot };

function ParticleGlyph({ shape, testid }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext("2d");
        const S = 96;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = S * dpr;
        canvas.height = S * dpr;
        ctx.scale(dpr, dpr);

        const off = document.createElement("canvas");
        off.width = S;
        off.height = S;
        const octx = off.getContext("2d");
        octx.strokeStyle = "#ffffff";
        octx.fillStyle = "#ffffff";
        octx.lineWidth = 5;
        octx.lineCap = "round";
        octx.lineJoin = "round";
        shape(octx);
        const data = octx.getImageData(0, 0, S, S).data;
        const pts = [];
        for (let y = 0; y < S; y += 3) {
            for (let x = 0; x < S; x += 3) {
                if (data[(y * S + x) * 4 + 3] > 100) pts.push({ x, y, p: Math.random() * 6.2832 });
            }
        }

        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let t = 0;
        let raf;
        const draw = () => {
            ctx.clearRect(0, 0, S, S);
            for (let i = 0; i < pts.length; i += 1) {
                const pt = pts[i];
                ctx.fillStyle = `rgba(222, 184, 92, ${0.45 + 0.45 * Math.sin(t + pt.p)})`;
                ctx.fillRect(pt.x, pt.y, 1.8, 1.8);
            }
            if (!reduce) {
                t += 0.035;
                raf = requestAnimationFrame(draw);
            }
        };
        draw();
        return () => cancelAnimationFrame(raf);
    }, [shape]);

    return <canvas ref={canvasRef} data-testid={testid} className="w-20 h-20 sm:w-24 sm:h-24" aria-hidden="true" />;
}

const FLOOR_PATTERNS = {
    "02": { active: [0, 6, 13, 17, 21], occupied: 5, rooms: [{ id: "MR-1", booked: false }, { id: "MR-2", booked: true }] },
    "03": { active: [1, 4, 8, 11, 14, 19, 22], occupied: 7, rooms: [{ id: "MR-1", booked: true }, { id: "MR-3", booked: false }] },
    "04": { active: [2, 3, 7, 10, 12, 16, 18, 20, 23], occupied: 9, rooms: [{ id: "MR-2", booked: true }, { id: "MR-4", booked: true }] },
    "06": { active: [5, 9, 15, 22], occupied: 4, rooms: [{ id: "MR-5", booked: false }, { id: "MR-6", booked: false }] },
};
const FLOOR_KEYS = Object.keys(FLOOR_PATTERNS);

function DeskGridVisual() {
    const reduce = useReducedMotion();
    const [floor, setFloor] = useState("03");
    const [paused, setPaused] = useState(false);
    const pattern = FLOOR_PATTERNS[floor];

    useEffect(() => {
        if (paused || reduce) return undefined;
        const id = setInterval(() => {
            setFloor((cur) => FLOOR_KEYS[(FLOOR_KEYS.indexOf(cur) + 1) % FLOOR_KEYS.length]);
        }, 4000);
        return () => clearInterval(id);
    }, [paused, reduce]);

    const pick = (f) => {
        setFloor(f);
        setPaused(true);
    };

    return (
        <div data-testid="floor-plan-mini-grid" className="rounded-2xl bg-[var(--accent-soft)] border border-[var(--accent)]/20 p-3 sm:p-4">
            <div className="flex items-center gap-0.5 mb-2" role="tablist" aria-label="Select floor">
                {FLOOR_KEYS.map((f) => (
                    <button
                        key={f}
                        type="button"
                        role="tab"
                        aria-selected={floor === f}
                        data-testid={`floor-tab-${f}`}
                        onClick={() => pick(f)}
                        className="min-w-[44px] min-h-[44px] -my-2 grid place-items-center"
                    >
                        <span
                            className={`px-2.5 py-1 rounded-md mono text-[11px] tracking-[0.1em] border transition-colors duration-200 ${
                                floor === f
                                    ? "border-[var(--accent)]/50 bg-[var(--paper)]/80 text-[var(--accent)] font-semibold"
                                    : "border-transparent text-[var(--ink-3)] hover:text-[var(--accent)]"
                            }`}
                        >
                            F{f}
                        </span>
                    </button>
                ))}
            </div>
            <div key={floor} className="grid grid-cols-6 gap-1.5">
                {Array.from({ length: 24 }).map((_, i) => {
                    const isActive = pattern.active.includes(i);
                    return (
                        <motion.span
                            key={`${floor}-${i}`}
                            className="block h-3.5 sm:h-4 rounded-[3px] border border-[var(--accent)]/35"
                            initial={reduce ? false : { opacity: 0, scale: 0.6 }}
                            animate={{
                                opacity: 1,
                                scale: 1,
                                backgroundColor: isActive
                                    ? ["rgba(212,169,60,0.06)", "rgba(212,169,60,0.35)", "rgba(212,169,60,0.06)"]
                                    : ["rgba(212,169,60,0)", "rgba(212,169,60,0.09)", "rgba(212,169,60,0)"],
                            }}
                            transition={
                                reduce
                                    ? { duration: 0 }
                                    : {
                                          opacity: { duration: 0.3, delay: i * 0.03 },
                                          scale: { duration: 0.3, delay: i * 0.03 },
                                          backgroundColor: { duration: 5, delay: 0.3 + i * 0.28, repeat: Infinity, ease: "easeInOut" },
                                      }
                            }
                        />
                    );
                })}
            </div>
            <div data-testid="floor-plan-mini-rooms" className="mt-2 flex items-center gap-1.5">
                {pattern.rooms.map((r) => (
                    <span
                        key={`${floor}-${r.id}`}
                        className="inline-flex items-center gap-1 rounded border border-[var(--accent)]/30 bg-[var(--paper)]/60 px-1.5 py-0.5 mono text-[8px] tracking-[0.12em] text-[var(--ink-3)]"
                    >
                        <span className={`w-1.5 h-1.5 rounded-full ${r.booked ? "bg-amber-500" : "bg-emerald-600"}`} />
                        {r.id}
                    </span>
                ))}
                <span className="ml-auto mono text-[8px] tracking-[0.12em] text-[var(--ink-3)] uppercase">
                    {pattern.rooms.filter((r) => !r.booked).length} free
                </span>
            </div>
            <div className="mt-2 flex items-center justify-between mono text-[9px] tracking-[0.18em] text-[var(--ink-3)] uppercase">
                <span data-testid="floor-plan-mini-caption">Floor {floor} · Live</span>
                <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
                    {pattern.occupied} occupied
                </span>
            </div>
        </div>
    );
}

function IconPanel({ feature }) {
    const Icon = feature.icon;
    return (
        <div className="relative w-full aspect-[4/3] rounded-2xl bg-[var(--paper)] border border-[var(--line)] shadow-[0_4px_20px_-4px_rgba(60,32,8,0.07)] overflow-hidden">
            <div className="dot-grid absolute inset-0 opacity-50" />
            <span className="absolute top-4 left-4 mono text-xs tracking-[0.25em] text-[var(--ink-3)]">{feature.n} /</span>
            <span className="absolute top-4 right-4 mono text-[10px] tracking-[0.2em] text-[var(--accent)] border border-[var(--accent)]/30 rounded px-2 py-1">
                {feature.badge}
            </span>
            <span className="absolute bottom-4 left-4 w-4 h-4 border-l border-b border-[var(--line-strong)]" />
            <span className="absolute bottom-4 right-4 w-4 h-4 border-r border-b border-[var(--line-strong)]" />
            <div className="absolute inset-0 grid place-items-center">
                {feature.n === "03" ? (
                    <DeskGridVisual />
                ) : GLYPHS[feature.n] ? (
                    <div className="rounded-2xl bg-[var(--accent-soft)] border border-[var(--accent)]/20 grid place-items-center">
                        <ParticleGlyph shape={GLYPHS[feature.n]} testid={`feature-glyph-${feature.n}`} />
                    </div>
                ) : (
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-[var(--accent-soft)] border border-[var(--accent)]/20 grid place-items-center text-[var(--accent)]">
                        <Icon size={40} strokeWidth={1.6} />
                    </div>
                )}
            </div>
        </div>
    );
}

function FeatureRow({ feature, index }) {
    const reduce = useReducedMotion();
    const flipped = index % 2 === 1;
    const dir = flipped ? 1 : -1;
    const t = { duration: 0.65, ease: EASE };
    const init = (x) => ({ opacity: 0, x: reduce ? 0 : x, y: reduce ? 0 : 20 });
    const view = { opacity: 1, x: 0, y: 0 };

    return (
        <div
            id={feature.id}
            data-testid={`feature-row-${feature.n}`}
            className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-16 scroll-mt-24 ${flipped ? "lg:flex-row-reverse" : ""}`}
        >
            <motion.div
                className="w-full lg:w-1/2"
                initial={init(dir * 48)}
                whileInView={view}
                viewport={{ once: true, margin: "-80px" }}
                transition={t}
            >
                <IconPanel feature={feature} />
            </motion.div>
            <motion.div
                className="w-full lg:w-1/2"
                initial={init(dir * -48)}
                whileInView={view}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ ...t, delay: 0.08 }}
            >
                <span className="mono text-xs tracking-[0.3em] text-[var(--accent)] font-medium">{feature.n} — {feature.badge}</span>
                <h3 className="mt-3 font-display font-bold text-2xl sm:text-3xl tracking-tight text-[var(--ink)]">
                    {feature.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-[var(--ink-2)] max-w-lg">{feature.body}</p>
            </motion.div>
        </div>
    );
}

export function Features() {
    return (
        <section id="features" data-testid="features-section" className="relative py-20 sm:py-28 scroll-mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal className="mb-14 sm:mb-20">
                    <Eyebrow>02 · Capabilities</Eyebrow>
                    <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[var(--ink)]">
                        What IDeaSpace Does
                    </h2>
                </Reveal>
                <div className="flex flex-col gap-16 sm:gap-24">
                    {FEATURES.map((f, i) => (
                        <FeatureRow key={f.n} feature={f} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
